import React, { useEffect, useMemo, useState } from "react";
import { generateClient } from "aws-amplify/api";
import { listAssessmentScores } from "../graphql/queries.js";

const client = generateClient();
export const EXPLORE_RELATIONSHIPS_READY = false;
const DOMAIN_ORDER = [
  "MentalHealth",
  "PhysicalHealth",
  "SpiritualHealth",
  "SocialHealth",
  "CopingMechanisms",
  "Trauma",
  "Demographics",
];
const DOMAIN_META = {
  MentalHealth: { color: "#0067B3", label: "Mental Health" },
  PhysicalHealth: { color: "#2F5D50", label: "Physical Health" },
  SpiritualHealth: {
    color: "#D4B65F",
    label: "Spiritual Health",
  },
  SocialHealth: { color: "#355070", label: "Social Health" },
  CopingMechanisms: {
    color: "#6F8F7B",
    label: "Coping Mechanisms",
  },
  Trauma: { color: "#8C1456", label: "Trauma" },
  Demographics: {
    color: "#5B4B8A",
    label: "Social Determinants of Health",
  },
};

// EXHAUSTIVE TRANSLATION DICTIONARY FROM ASSESSMENT.JS
const HUMAN_EXPLANATIONS = {
  TRHurtfulNames: "Hurtful Names",
  TRNoNurturing: "No Nurturing",
  TRHit: "Hit or Physical Abuse",
  TRNeedsNotMet: "Needs Not Met or Neglect",
  TRForcedSex: "Forced Sex or Rape",
  TRResponsible: "Adult Responsibilities as a Child",
  TRViolence: "Witnessed Violence",
  TRSubstanceAbuse: "WitnessedSubstance Abuse",
  TRMentalIllness: "WitnessedMental Illness",
  TRParentDivorce: "Witnessed Parental Divorce",
  TRParentIncarcerated: "Parent Incarcerated",
  TRHomelessness: "Consistent Homelessness",
  TRBodyFunction: "Loss of Body Function or Mobility",
  TRNaturalDisaster: "Natural Disaster",
  TRDirectTerrorism: "Direct Terrorism",
  TRIndirectTerrorism: "Indirect Terrorism",
  TRLovedOne: "Loss of Loved One",

  TRBelief: "Identifies as Having Trauma",
  TRResponseProfessional: "Talked to a Professional",
  TRResponseFamilyFriend: "Talked to Family/Friends",
  TRResponseGod: "Talked to God",
  TRResponseNoOne: "Did Not Tell Anyone",
  TRNoTrauma: "Reported No Trauma History",
  TRScore: "Overall Trauma Score",

  // --- COPING MECHANISMS ---
  CMAlcohol: "Alcohol Abuse",
  CMDrugs: "Drug Abuse",
  CMOvereating: "Overeating",
  CMSmoking: "Smoking",
  CMGaming: "Excessive Gaming",
  CMShopping: "Excessive Shopping",
  CMGambling: "Gambling",
  CMSex: "Casual Sex",
  CMPorn: "Pornography",
  CMBlaming: "Blaming Self or Others",
  CMHurting: "Hurting Self or Others",
  CMDisengage: "Disengagement / Detachment / Self Isolation",
  CMArt: "Drawing/Painting/Art",
  CMMusic: "Music",
  CMPoetry: "Poetry/Poetical Lyrics",
  CMReading: "Reading",
  CMGroups: "Group Discussion",
  CMCounseling: "Individual Counseling",
  CMVenting: "Talking/Venting",
  CMWriting: "Writing/Journaling",
  CMSensory: "Sensory/Fidgets",
  CMDancing: "Dancing",
  CMExercising: "Exercising",
  CMWalking: "Walking",
  CMChange: "Change Something for Different Results",
  CMAnalyze: "Analyze the Situation for Better Understanding",
  CMDaydream: "Daydream or Imagine a Better Situation",
  CMPositive: "Focus on the Positives",
  CMScore: "Overall Coping Mechanism Score",

  // --- MENTAL HEALTH ---
  MHDxPTSD: "PTSD Diagnosis",
  MHDxDepression: "Depression Diagnosis",
  MHDxSUD: "Substance Use Disorder Diagnosis",
  MHDxOtherMental: "Other Mental Health Diagnosis",
  MHSleeplessness: "Sleeplessness",
  MHAlcoholUse: "Alcohol Use",
  MHAnxiety: "Fight, Flight, Freeze",
  MHDepression: "Depression",
  MHDrugUse: "Drug Use",
  MHGrief: "Grief",
  MHGuilt: "Guilt",
  MHIrritability: "Irritability",
  MHStress: "Stress",
  MHRegret: "Regret",
  MHSuicidalThoughts: "Suicidal Thoughts",
  MHLoneliness: "Loneliness",
  MHWorry: "Worry / Anxiety",
  MHHope: "Hopelessness",
  MHPeace: "Inner Turmoil",
  MHJoy: "Emptiness",
  MHScore: "Overall Mental Health Score",

  // --- PHYSICAL HEALTH ---
  PHDxInfertility: "Infertility Diagnosis",
  PHDxCurableSTD: "Curable STD Diagnosis",
  PHDxIncurableSTD: "Incurable STD Diagnosis",
  PHDxCancer: "Cancer Diagnosis",
  PHDxDiabetes: "Diabetes Diagnosis",
  PHDxHighBlood: "High Blood Pressure Diagnosis",
  PHDxHeartDisease: "Heart Disease Diagnosis",
  PHDxIrritableBowel: "Irritable Bowel Syndrome Diagnosis",
  PHDxVitA: "Vitamin A Deficiency",
  PHDxVitB: "Vitamin B Deficiency",
  PHDxVitC: "Vitamin C Deficiency",
  PHDxVitD: "Vitamin D Deficiency",
  PHDxVitE: "Vitamin E Deficiency",
  PHDxVitK: "Vitamin K Deficiency",
  PHDxAutoimmune: "Autoimmune Diagnosis",
  PHDxOtherPhysical: "Other Physical Diagnosis",
  PHVegeFruits: "Vegetables and Fruit",
  PHBeanLentils: "Beans and Lentils",
  PHGrainBreads: "Grains and Breads",
  PHDairy: "Dairy",
  PHMeat: "Animal Meat",
  PHFishSeafood: "Fish/Seafood",
  PHSweets: "Sweets/Desserts",
  PHWater: "Water",
  PHPhysicalActivity: "Physical Activity",
  PHScore: "Overall Physical Health Score",

  // --- SPIRITUAL HEALTH ---
  SHSpiritualDefine: "God or a Higher Power Loves Me",
  SHSpiritualIntegrate: "Integrating Spirituality Into Life",
  SHPrayer: "Prayer",
  SHSpiritualActivity: "Communication With Like-Minded Individuals",
  SHReadText: "Reading Religious Text",
  SHAlignText: "Aligning Life With Religious Texts",
  SHCommunity: "Participating in Religious Gatherings / Groups / Prayer",
  SHScore: "Overall Spiritual Health Score",

  // --- SOCIAL SATISFACTION & LIFE SATISFACTION (SoH) ---
  SoHLearning: "Willingness to Learn and Be Corrected",
  SoHStable: "Stability (e.g., Housing, Finances, etc.)",
  SoHSafety: "Safety and Security",
  SoHKindness: "Kindness Toward Others",
  SoHForgiveness: "Forgiveness of Self and Others",
  SoHPatience: "Patience With Self and Others",
  SoHRelationships: "Support in Relationships",
  SoHContribution: "Contributing to Others / Community",
  SoHBoundaries: "Maintaining Healthy Boundaries",
  SoHEUnpleasant: "Unpleasant Emotions",
  SoHEPleasant: "Pleasant Emotions",
  SoHEControl: "Control Over Emotions",
  SoHScore: "Overall Social Health Score",

  // --- BACKGROUND SDOH COMPREHENSIVE PARAMETERS ---
  SDoHAgeRange: "Age Range",
  SDoHRace: "Race",
  SDoHGeographicRegion: "Geographic Region",
  SDoHEthnicity: "Ethnicity",
  SDoHState: "State",
  SDoHZipCode: "Zip Code",
  SDoHMaritalStatus: "Marital Status",
  SDoHMilitaryStatus: "Military Status",
  SDoHGender: "Gender",
  SDoHEducation: "Highest Education Level",
  SDoHJobStatus: "Job Status",
  SDoHIncome: "Income Level",
  SDoHHousingStatus: "Housing Status",
  SDoHHomeAsChild: "Home Environment as a Child",
  SDoHReligion: "Religion",
};

const FORBIDDEN_FIELDS = new Set([
  "id",
  "isActive",
  "organizationId",
  "userId",
  "SDoHDenomination",
]);

const FALLBACK_SURVEY_SEED = [
  {
    TRHurtfulNames: 1,
    TRHit: 1,
    TRResponseNoOne: 1,
    TRScore: "5",
    CMAlcohol: "5+x Week",
    CMArt: "3-4x Week",
    MHAnxiety: "5+x Week",
    MHStress: "5+x Week",
    MHScore: 8,
    SoHStable: "Disagree",
    SoHEUnpleasant: "Strongly Agree",
    SDoHAgeRange: "25-34",
    SDoHIncome: "$50,000 - $74,999",
    SDoHMaritalStatus: "Single",
    SDoHEducation: "Bachelor's Degree",
    SDoHReligion: "None",
    SDoHGeographicRegion: "United States",
  },
];

export default function ExploreRelationships() {
  const [databaseDataset, setDatabaseDataset] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // New state to toggle show/hide the instructions to save substantial vertical space
  const [showInstructions, setShowInstructions] = useState(false);

  const [selectedMetric, setSelectedMetric] = useState("");
  const [hoveredMetric, setHoveredMetric] = useState(null);
  const [filterQuery, setFilterQuery] = useState("");
  const [minCorrelation, setMinCorrelation] = useState(0.5);
  const [selectedDomain, setSelectedDomain] = useState("MentalHealth");
  const [viewMode, setViewMode] = useState("constellation");

  const [demographicFilters, setDemographicFilters] = useState({
    ageRange: "",
    income: "",
    maritalStatus: "",
    education: "",
    religion: "",
    geographicRegion: "",
  });

  useEffect(() => {
    async function fetchLiveRecords() {
      try {
        setLoading(true);
        const response = await client.graphql({
          query: listAssessmentScores,
          variables: { limit: 2500 },
        });
        const rawItems = response?.data?.listAssessmentScores?.items || [];
        const observedUserIds = new Set();
        const verifiedUniqueRecords = [];

        for (const item of rawItems) {
          if (!item) continue;
          const identityKey = item.userId || item.id;
          if (identityKey && !observedUserIds.has(identityKey)) {
            observedUserIds.add(identityKey);
            verifiedUniqueRecords.push(item);
          }
          if (verifiedUniqueRecords.length >= 2500) break;
        }
        setDatabaseDataset(verifiedUniqueRecords);
      } catch (err) {
        console.error("Database resolution anomaly:", err);
        setError("Unable to compile live database paths safely.");
      } finally {
        setLoading(false);
      }
    }
    fetchLiveRecords();
  }, []);

  const dataset = useMemo(() => {
    return databaseDataset.length > 0 ? databaseDataset : FALLBACK_SURVEY_SEED;
  }, [databaseDataset]);

  const uniqueDropdownValues = useMemo(() => {
    const lists = { age: [], inc: [], mar: [], edu: [], rel: [], geo: [] };
    dataset.forEach((row) => {
      if (row.SDoHAgeRange && !lists.age.includes(row.SDoHAgeRange))
        lists.age.push(row.SDoHAgeRange);
      if (row.SDoHIncome && !lists.inc.includes(row.SDoHIncome))
        lists.inc.push(row.SDoHIncome);
      if (row.SDoHMaritalStatus && !lists.mar.includes(row.SDoHMaritalStatus))
        lists.mar.push(row.SDoHMaritalStatus);
      if (row.SDoHEducation && !lists.edu.includes(row.SDoHEducation))
        lists.edu.push(row.SDoHEducation);
      if (row.SDoHReligion && !lists.rel.includes(row.SDoHReligion))
        lists.rel.push(row.SDoHReligion);
      if (
        row.SDoHGeographicRegion &&
        !lists.geo.includes(row.SDoHGeographicRegion)
      )
        lists.geo.push(row.SDoHGeographicRegion);
    });
    return lists;
  }, [dataset]);

  const filteredDataset = useMemo(() => {
    return dataset.filter((row) => {
      if (
        demographicFilters.ageRange &&
        row.SDoHAgeRange !== demographicFilters.ageRange
      )
        return false;
      if (
        demographicFilters.income &&
        row.SDoHIncome !== demographicFilters.income
      )
        return false;
      if (
        demographicFilters.maritalStatus &&
        row.SDoHMaritalStatus !== demographicFilters.maritalStatus
      )
        return false;
      if (
        demographicFilters.education &&
        row.SDoHEducation !== demographicFilters.education
      )
        return false;
      if (
        demographicFilters.religion &&
        row.SDoHReligion !== demographicFilters.religion
      )
        return false;
      if (
        demographicFilters.geographicRegion &&
        row.SDoHGeographicRegion !== demographicFilters.geographicRegion
      )
        return false;
      return true;
    });
  }, [dataset, demographicFilters]);

  const parsedDataVectors = useMemo(() => {
    if (!filteredDataset || filteredDataset.length === 0)
      return { keys: [], vectors: {} };
    const activeKeys = Object.keys(HUMAN_EXPLANATIONS).filter(
      (key) => key in filteredDataset[0] && !FORBIDDEN_FIELDS.has(key),
    );

    const vectors = {};
    activeKeys.forEach((key) => {
      vectors[key] = filteredDataset.map((row) => {
        const val = row[key];
        if (val === undefined || val === null) return 0;
        if (typeof val === "number") return val;
        if (typeof val === "boolean") return val ? 1 : 0;

        const text = String(val).trim();
        if (text === "5+x Week") return 4;
        if (text === "3-4x Week") return 3;
        if (text === "1-2x Week") return 2;
        if (text === "1-2x Month") return 1;
        if (text === "N/A-Rare" || text === "No Answer") return 0;

        if (
          key.startsWith("CM") &&
          ![
            "CMAlcohol",
            "CMDrugs",
            "CMOvereating",
            "CMSmoking",
            "CMGaming",
            "CMShopping",
            "CMGambling",
            "CMSex",
            "CMPorn",
            "CMBlaming",
            "CMHurting",
            "CMDisengage",
          ].includes(key)
        ) {
          if (text === "3-4x Week") return 0.01;
          if (text === "1-2x Week") return 0.02;
          if (text === "1-2x Month") return 0.03;
          if (text === "N/A-Rare") return 0.04;
        }

        if (text === "Strongly Agree") return 4;
        if (text === "Agree") return 3;
        if (text === "Neither") return 2;
        if (text === "Disagree") return 1;
        if (text === "Strongly Disagree") return 0;

        const floatParsed = parseFloat(text);
        return isNaN(floatParsed) ? 0 : floatParsed;
      });
    });
    return { keys: activeKeys, vectors };
  }, [filteredDataset]);

  const assessmentEdges = useMemo(() => {
    const { keys, vectors } = parsedDataVectors;
    if (keys.length === 0) return [];
    const calculatedEdges = [];

    // Check if we are dealing with a single-record edge-case
    const isSingleRecord = filteredDataset.length === 1;

    for (let i = 0; i < keys.length; i++) {
      for (let j = i + 1; j < keys.length; j++) {
        const k1 = keys[i];
        const k2 = keys[j];

        let rValue = 0;

        if (isSingleRecord) {
          // --- SINGLE RECORD FALLBACK RULE ---
          // Grab the single numerical value for both metrics
          const val1 = vectors[k1][0] || 0;
          const val2 = vectors[k2][0] || 0;

          // If both metrics are present/active (greater than 0),
          // treat them as a 100% intentional co-occurrence match.
          if (val1 > 0 && val2 > 0) {
            rValue = 1.0;
          } else {
            rValue = 0;
          }
        } else {
          // Standard multi-record Pearson calculation
          rValue = calculatePearsonCorrelation(vectors[k1], vectors[k2]);
        }

        if (!isNaN(rValue) && Math.abs(rValue) > 0.01) {
          calculatedEdges.push({
            id: `${k1}---${k2}`,
            from: k1,
            to: k2,
            r: rValue,
            absR: Math.abs(rValue),
          });
        }
      }
    }
    return calculatedEdges;
  }, [parsedDataVectors, filteredDataset]);

  const sortedDomainCategories = useMemo(() => {
    const categories = {
      Trauma: [],
      CopingMechanisms: [],
      MentalHealth: [],
      PhysicalHealth: [],
      SpiritualHealth: [],
      SocialHealth: [],
      Demographics: [],
    };
    parsedDataVectors.keys.forEach((key) => {
      if (key.startsWith("TR")) categories.Trauma?.push(key);
      else if (key.startsWith("CM")) categories.CopingMechanisms?.push(key);
      else if (key.startsWith("MH")) categories.MentalHealth?.push(key);
      else if (key.startsWith("PH")) categories.PhysicalHealth?.push(key);
      else if (key.startsWith("SH")) categories.SpiritualHealth?.push(key);
      else if (key.startsWith("SoH")) categories.SocialHealth?.push(key);
      else if (key.startsWith("SDoH")) categories.Demographics?.push(key);
    });
    return categories;
  }, [parsedDataVectors]);

  const activeFocusId = selectedMetric || hoveredMetric;

  const connectedEdgesList = useMemo(() => {
    if (!activeFocusId) return [];
    return assessmentEdges
      .filter(
        (edge) =>
          (edge.from === activeFocusId || edge.to === activeFocusId) &&
          edge.absR >= minCorrelation,
      )
      .sort((a, b) => b.absR - a.absR);
  }, [activeFocusId, assessmentEdges, minCorrelation]);

  const proximityMap = useMemo(() => {
    if (!activeFocusId) return null;
    const map = { [activeFocusId]: true };
    connectedEdgesList.forEach((e) => {
      map[e.from] = true;
      map[e.to] = true;
    });
    return map;
  }, [activeFocusId, connectedEdgesList]);

  const filteredFieldsByDomain = useMemo(() => {
    const output = {};
    Object.entries(sortedDomainCategories).forEach(([domain, fields]) => {
      output[domain] = (fields || []).filter((field) => {
        const translation = HUMAN_EXPLANATIONS[field] || "";
        const q = filterQuery.toLowerCase();
        return (
          field.toLowerCase().includes(q) ||
          translation.toLowerCase().includes(q)
        );
      });
    });
    return output;
  }, [sortedDomainCategories, filterQuery]);

  useEffect(() => {
    const current = filteredFieldsByDomain[selectedDomain] || [];
    if (current.length > 0) return;
    const next = Object.keys(filteredFieldsByDomain).find(
      (d) => (filteredFieldsByDomain[d] || []).length > 0,
    );
    if (next) setSelectedDomain(next);
  }, [filteredFieldsByDomain, selectedDomain]);

  const visibleFieldCount = useMemo(
    () =>
      Object.values(filteredFieldsByDomain).reduce(
        (sum, arr) => sum + arr.length,
        0,
      ),
    [filteredFieldsByDomain],
  );

  const domainStrengthRows = useMemo(() => {
    return DOMAIN_ORDER.map((domain) => {
      const fields = filteredFieldsByDomain[domain] || [];
      const edgeHits = assessmentEdges.filter(
        (e) =>
          fields.includes(e.from) &&
          fields.includes(e.to) &&
          e.absR >= minCorrelation,
      ).length;
      return {
        domain,
        fields: fields.length,
        edgeHits,
        intensity: Math.min(
          100,
          Math.round((edgeHits / Math.max(1, fields.length)) * 30),
        ),
      };
    }).filter((row) => row.fields > 0);
  }, [filteredFieldsByDomain, assessmentEdges, minCorrelation]);

  const metricDomainMap = useMemo(() => {
    const map = {};
    DOMAIN_ORDER.forEach((domain) => {
      (sortedDomainCategories[domain] || []).forEach((field) => {
        map[field] = domain;
      });
    });
    return map;
  }, [sortedDomainCategories]);

  const groupedFocusMetricOptions = useMemo(() => {
    return DOMAIN_ORDER.map((domain) => {
      const meta = DOMAIN_META[domain] || { label: domain };
      const options = (filteredFieldsByDomain[domain] || [])
        .map((field) => ({
          value: field,
          label: HUMAN_EXPLANATIONS[field] || field,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      return {
        domain,
        label: meta.label,
        options,
      };
    }).filter((group) => group.options.length > 0);
  }, [filteredFieldsByDomain]);

  const constellationFocusNodes = useMemo(() => {
    if (!activeFocusId) return [];

    const maxNodes = 12;
    const edges = connectedEdgesList.slice(0, maxNodes);
    if (edges.length === 0) return [];

    const centerX = 50;
    const centerY = 50;
    const ring = 35;

    return edges.map((edge, idx) => {
      const field = edge.from === activeFocusId ? edge.to : edge.from;
      const angle = (idx / Math.max(1, edges.length)) * Math.PI * 2;
      const influence = Math.max(0.25, Math.min(1, edge.absR));
      const size = 48 + influence * 26;

      const x = centerX + ring * Math.cos(angle);
      const y = centerY + ring * Math.sin(angle);

      return {
        id: field,
        x,
        y,
        size,
        label: HUMAN_EXPLANATIONS[field] || field,
        r: edge.r,
        absR: edge.absR,
        domain: metricDomainMap[field] || "Demographics",
      };
    });
  }, [activeFocusId, connectedEdgesList, metricDomainMap]);

  const topRelationships = useMemo(() => {
    return [...assessmentEdges]
      .filter((e) => e.absR >= minCorrelation)
      .sort((a, b) => b.absR - a.absR)
      .slice(0, 6);
  }, [assessmentEdges, minCorrelation]);

  const focusQuickInsights = useMemo(() => {
    if (!activeFocusId || connectedEdgesList.length === 0) {
      return { strongestPositive: null, strongestInverse: null };
    }

    const strongestPositive = connectedEdgesList
      .filter((e) => e.r > 0)
      .sort((a, b) => b.absR - a.absR)[0];

    const strongestInverse = connectedEdgesList
      .filter((e) => e.r < 0)
      .sort((a, b) => b.absR - a.absR)[0];

    return { strongestPositive, strongestInverse };
  }, [activeFocusId, connectedEdgesList]);

  return (
    <div
      style={{
        padding: 12,
        background:
          "radial-gradient(circle at 15% 10%, #e6f5ff, #f8fafc 45%, #eef2ff)",
        fontFamily: "Segoe UI, Tahoma, Geneva, Verdana, sans-serif",
        color: "#0f172a",
        minHeight: "70vh",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          background: "linear-gradient(120deg, #0067B3, #355070)",
          color: "#fff",
          borderRadius: 10,
          padding: "12px 14px",
          boxShadow: "0 8px 24px rgba(0, 56, 102, 0.25)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>
              Relationship Explorer
            </div>
            <div style={{ fontSize: 12, opacity: 0.9 }}>
              Interactive constellation of how assessment dimensions travel
              together.
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              onClick={() => setViewMode("constellation")}
              style={{
                border: "none",
                borderRadius: 999,
                padding: "6px 10px",
                fontSize: 11,
                fontWeight: 700,
                cursor: "pointer",
                background:
                  viewMode === "constellation" ? "#ffffff" : "#94b8d8",
                color: viewMode === "constellation" ? "#0f172a" : "#16324f",
              }}
            >
              Constellation
            </button>
            <button
              onClick={() => setViewMode("matrix")}
              style={{
                border: "none",
                borderRadius: 999,
                padding: "6px 10px",
                fontSize: 11,
                fontWeight: 700,
                cursor: "pointer",
                background: viewMode === "matrix" ? "#ffffff" : "#94b8d8",
                color: viewMode === "matrix" ? "#0f172a" : "#16324f",
              }}
            >
              Matrix
            </button>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
            gap: 8,
            marginTop: 10,
          }}
        >
          <div
            style={{
              fontSize: 11,
              background: "#ffffff24",
              borderRadius: 8,
              padding: 8,
            }}
          >
            Records in view: <strong>{filteredDataset.length}</strong>
          </div>
          <div
            style={{
              fontSize: 11,
              background: "#ffffff24",
              borderRadius: 8,
              padding: 8,
            }}
          >
            Metrics visible: <strong>{visibleFieldCount}</strong>
          </div>
          <div
            style={{
              fontSize: 11,
              background: "#ffffff24",
              borderRadius: 8,
              padding: 8,
            }}
          >
            Edge cutoff: <strong>{(minCorrelation * 100).toFixed(0)}%</strong>
          </div>
          <div
            style={{
              fontSize: 11,
              background: "#ffffff24",
              borderRadius: 8,
              padding: 8,
            }}
          >
            {loading ? "Syncing assessment dataset..." : "Dataset synchronized"}
          </div>
        </div>
        {error && (
          <div
            style={{
              marginTop: 8,
              fontSize: 11,
              background: "#fff1f2",
              color: "#9f1239",
              border: "1px solid #fecdd3",
              borderRadius: 8,
              padding: "6px 8px",
            }}
          >
            {error}
          </div>
        )}
      </div>

      <div
        style={{
          background: "white",
          border: "1px solid #dbe4f0",
          borderRadius: 10,
          padding: "10px 12px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 8,
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={() => setShowInstructions((v) => !v)}
            style={{
              border: "none",
              background: "#eef5ff",
              color: "#0f172a",
              padding: "6px 10px",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {showInstructions ? "Hide Guide" : "Show Guide"}
          </button>

          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <select
              value={selectedMetric || ""}
              onChange={(e) => setSelectedMetric(e.target.value)}
              style={{
                padding: "6px 10px",
                borderRadius: 8,
                border: "1px solid #c9d8ea",
                fontSize: 12,
                minWidth: 240,
                background: "#fff",
              }}
            >
              <option value="">Select Focus Metric</option>
              {groupedFocusMetricOptions.map((group) => (
                <optgroup key={group.domain} label={group.label}>
                  {group.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <input
              type="text"
              placeholder="Find a metric..."
              value={filterQuery}
              onChange={(e) => setFilterQuery(e.target.value)}
              style={{
                padding: "6px 10px",
                borderRadius: 8,
                border: "1px solid #c9d8ea",
                fontSize: 12,
                minWidth: 220,
              }}
            />
          </div>
        </div>

        {showInstructions && (
          <div
            style={{
              marginBottom: 8,
              background: "#f8fbff",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              padding: "8px 10px",
              fontSize: 12,
              color: "#334155",
              lineHeight: 1.4,
            }}
          >
            Use demographic filters, choose a domain, then select a metric to
            light up its strongest correlation neighborhood. This is correlation
            intelligence, not causation.
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 8,
          }}
        >
          {Object.entries({
            ageRange: ["Age Bracket", uniqueDropdownValues.age],
            income: ["Income Tier", uniqueDropdownValues.inc],
            maritalStatus: ["Marital", uniqueDropdownValues.mar],
            education: ["Education", uniqueDropdownValues.edu],
            religion: ["Belief", uniqueDropdownValues.rel],
            geographicRegion: ["Region", uniqueDropdownValues.geo],
          }).map(([key, [label, options]]) => (
            <select
              key={key}
              value={demographicFilters[key]}
              onChange={(e) =>
                setDemographicFilters((prev) => ({
                  ...prev,
                  [key]: e.target.value,
                }))
              }
              style={{
                width: "100%",
                padding: "6px 8px",
                borderRadius: 8,
                border: "1px solid #cbd5e1",
                fontSize: 12,
                background: "#fff",
              }}
            >
              <option value="">All {label}</option>
              {options.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          ))}
        </div>

        <div
          style={{
            marginTop: 8,
            display: "flex",
            gap: 8,
            alignItems: "center",
          }}
        >
          <div style={{ fontSize: 11, color: "#475569", fontWeight: 700 }}>
            Correlation cutoff
          </div>
          <input
            type="range"
            min="0.5"
            max="0.99"
            step="0.01"
            value={minCorrelation}
            onChange={(e) => setMinCorrelation(parseFloat(e.target.value))}
            style={{ width: 180, accentColor: "#0067B3" }}
          />
          <div style={{ fontSize: 11, color: "#1e293b", fontWeight: 700 }}>
            {(minCorrelation * 100).toFixed(0)}%
          </div>
          <button
            onClick={() =>
              setDemographicFilters({
                ageRange: "",
                income: "",
                maritalStatus: "",
                education: "",
                religion: "",
                geographicRegion: "",
              })
            }
            style={{
              marginLeft: "auto",
              border: "1px solid #cbd5e1",
              background: "white",
              borderRadius: 8,
              fontSize: 11,
              fontWeight: 700,
              padding: "6px 10px",
              cursor: "pointer",
            }}
          >
            Reset Filters
          </button>
        </div>
      </div>

      <div
        className="rel-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "260px 1fr 340px",
          gap: 10,
          flex: 1,
          minHeight: 0,
        }}
      >
        <div
          style={{
            background: "white",
            border: "1px solid #dbe4f0",
            borderRadius: 10,
            padding: 10,
            overflowY: "auto",
            minHeight: 0,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>
            Domains
          </div>
          {domainStrengthRows.map((row) => {
            const meta = DOMAIN_META[row.domain] || {
              color: "#64748b",
              icon: "•",
              label: row.domain,
            };
            const active = selectedDomain === row.domain;
            return (
              <button
                key={row.domain}
                onClick={() => setSelectedDomain(row.domain)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  marginBottom: 8,
                  borderRadius: 8,
                  border: active
                    ? `2px solid ${meta.color}`
                    : "1px solid #e2e8f0",
                  background: active ? "#f8fbff" : "#fff",
                  padding: "8px 9px",
                  cursor: "pointer",
                }}
              >
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span style={{ fontSize: 12, fontWeight: 700 }}>
                    {meta.icon} {meta.label}
                  </span>
                  <span style={{ fontSize: 11, color: "#64748b" }}>
                    {row.fields}
                  </span>
                </div>
                <div
                  style={{
                    marginTop: 6,
                    width: "100%",
                    height: 6,
                    background: "#edf2f7",
                    borderRadius: 999,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${row.intensity}%`,
                      height: "100%",
                      background: meta.color,
                    }}
                  />
                </div>
              </button>
            );
          })}
        </div>

        <div
          style={{
            background: "white",
            border: "1px solid #dbe4f0",
            borderRadius: 10,
            padding: 10,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>
            {viewMode === "constellation"
              ? "Metric Constellation"
              : "Metric Matrix"}
          </div>

          {viewMode === "constellation" ? (
            <div
              style={{
                position: "relative",
                flex: 1,
                borderRadius: 10,
                background:
                  "radial-gradient(circle at 20% 20%, #f5fbff, #eef3ff 55%, #f8fafc)",
                border: "1px dashed #c5d5ea",
                overflow: "hidden",
              }}
            >
              {activeFocusId && (
                <div
                  style={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    width: 148,
                    textAlign: "center",
                    zIndex: 3,
                  }}
                >
                  <div
                    style={{
                      width: 96,
                      height: 96,
                      borderRadius: "50%",
                      margin: "0 auto",
                      border: "3px solid #0067B3",
                      background: "#fff",
                      boxShadow:
                        "0 0 0 8px #0067B322, 0 10px 20px rgba(15,23,42,0.18)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 8,
                      fontSize: 11,
                      fontWeight: 700,
                      lineHeight: 1.2,
                    }}
                  >
                    Focus
                  </div>
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#0f172a",
                      lineHeight: 1.2,
                    }}
                  >
                    {HUMAN_EXPLANATIONS[activeFocusId] || activeFocusId}
                  </div>
                </div>
              )}

              {constellationFocusNodes.map((node) => {
                const meta = DOMAIN_META[node.domain] || {
                  color: "#64748b",
                  label: node.domain,
                };
                const positive = node.r > 0;
                const stroke = positive ? "#0ea5e9" : "#ef4444";

                return (
                  <div
                    key={node.id}
                    style={{
                      position: "absolute",
                      left: `${node.x}%`,
                      top: `${node.y}%`,
                      transform: "translate(-50%, -50%)",
                      textAlign: "center",
                      width: 150,
                      zIndex: 4,
                    }}
                  >
                    <button
                      title={node.label}
                      onMouseEnter={() => setHoveredMetric(node.id)}
                      onMouseLeave={() => setHoveredMetric(null)}
                      onClick={() => setSelectedMetric(node.id)}
                      style={{
                        width: node.size,
                        height: node.size,
                        borderRadius: "50%",
                        border: `2px solid ${meta.color}`,
                        background: "#fff",
                        color: "#0f172a",
                        fontSize: 10,
                        fontWeight: 700,
                        padding: 6,
                        cursor: "pointer",
                        boxShadow: `0 0 0 5px ${meta.color}1f, 0 6px 14px rgba(15,23,42,0.14)`,
                        lineHeight: 1.1,
                      }}
                    >
                      {(node.absR * 100).toFixed(0)}%
                    </button>
                    <div
                      style={{
                        marginTop: 5,
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#334155",
                        lineHeight: 1.2,
                        wordBreak: "break-word",
                      }}
                    >
                      {node.label}
                    </div>
                    <div
                      style={{
                        fontSize: 9,
                        color: stroke,
                        fontWeight: 700,
                        marginTop: 2,
                      }}
                    >
                      {positive ? "Positive link" : "Inverse link"}
                    </div>
                  </div>
                );
              })}

              <div
                style={{
                  position: "absolute",
                  left: 10,
                  top: 10,
                  background: "#ffffffd9",
                  border: "1px solid #dbe4f0",
                  borderRadius: 8,
                  padding: "7px 8px",
                  fontSize: 10,
                  color: "#334155",
                  maxWidth: 280,
                  lineHeight: 1.3,
                }}
              >
                {activeFocusId ? (
                  <>
                    {focusQuickInsights.strongestPositive && (
                      <div>
                        Strongest positive:{" "}
                        <strong>
                          {HUMAN_EXPLANATIONS[
                            focusQuickInsights.strongestPositive.from ===
                            activeFocusId
                              ? focusQuickInsights.strongestPositive.to
                              : focusQuickInsights.strongestPositive.from
                          ] ||
                            (focusQuickInsights.strongestPositive.from ===
                            activeFocusId
                              ? focusQuickInsights.strongestPositive.to
                              : focusQuickInsights.strongestPositive.from)}
                        </strong>{" "}
                        (
                        {(
                          focusQuickInsights.strongestPositive.absR * 100
                        ).toFixed(0)}
                        %)
                      </div>
                    )}
                    {focusQuickInsights.strongestInverse && (
                      <div style={{ marginTop: 4 }}>
                        Strongest inverse:{" "}
                        <strong>
                          {HUMAN_EXPLANATIONS[
                            focusQuickInsights.strongestInverse.from ===
                            activeFocusId
                              ? focusQuickInsights.strongestInverse.to
                              : focusQuickInsights.strongestInverse.from
                          ] ||
                            (focusQuickInsights.strongestInverse.from ===
                            activeFocusId
                              ? focusQuickInsights.strongestInverse.to
                              : focusQuickInsights.strongestInverse.from)}
                        </strong>{" "}
                        (
                        {(
                          focusQuickInsights.strongestInverse.absR * 100
                        ).toFixed(0)}
                        %)
                      </div>
                    )}
                  </>
                ) : (
                  "Select a focus metric to surface strongest positive and inverse relationships."
                )}
              </div>

              <div
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  width: 280,
                  height: 280,
                  borderRadius: "50%",
                  border: "1px dashed #c5d5ea",
                  pointerEvents: "none",
                }}
              />

              <div
                style={{
                  position: "absolute",
                  right: 10,
                  bottom: 10,
                  background: "#ffffffcc",
                  border: "1px solid #dbe4f0",
                  borderRadius: 8,
                  padding: "6px 8px",
                  fontSize: 11,
                  color: "#334155",
                }}
              >
                {!activeFocusId
                  ? "Choose a focus metric to map correlations."
                  : "Satellite % = absolute correlation strength with focus metric."}
              </div>
            </div>
          ) : (
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 8,
                paddingRight: 4,
              }}
            >
              {Object.entries(filteredFieldsByDomain).map(
                ([domain, fields]) => {
                  if (fields.length === 0) return null;
                  const meta = DOMAIN_META[domain] || {
                    color: "#64748b",
                    icon: "•",
                    label: domain,
                  };
                  return (
                    <div
                      key={domain}
                      style={{
                        border: "1px solid #e2e8f0",
                        borderLeft: `4px solid ${meta.color}`,
                        borderRadius: 8,
                        padding: 8,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          marginBottom: 6,
                        }}
                      >
                        {meta.icon} {meta.label}
                      </div>
                      <div
                        style={{ display: "flex", flexWrap: "wrap", gap: 5 }}
                      >
                        {fields.map((field) => {
                          const isActiveTarget = field === activeFocusId;
                          const linked = proximityMap
                            ? proximityMap[field]
                            : false;
                          return (
                            <button
                              key={field}
                              onMouseEnter={() => setHoveredMetric(field)}
                              onMouseLeave={() => setHoveredMetric(null)}
                              onClick={() =>
                                setSelectedMetric((prev) =>
                                  prev === field ? "" : field,
                                )
                              }
                              title={HUMAN_EXPLANATIONS[field] || field}
                              style={{
                                padding: "4px 8px",
                                borderRadius: 999,
                                border: isActiveTarget
                                  ? `2px solid ${meta.color}`
                                  : linked
                                    ? "1px solid #0067B3"
                                    : "1px solid #dbe4f0",
                                background: isActiveTarget
                                  ? "#eff6ff"
                                  : linked
                                    ? "#f8fbff"
                                    : "white",
                                fontSize: 10,
                                cursor: "pointer",
                              }}
                            >
                              {HUMAN_EXPLANATIONS[field] || field}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                },
              )}
            </div>
          )}
        </div>

        <div
          style={{
            background: "white",
            border: "1px solid #dbe4f0",
            borderRadius: 10,
            padding: 10,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>
            Insight Feed
          </div>

          {activeFocusId ? (
            <div
              style={{
                fontSize: 11,
                marginBottom: 8,
                color: "#334155",
                background: "#f8fbff",
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                padding: "7px 8px",
              }}
            >
              Focus metric: <strong>{HUMAN_EXPLANATIONS[activeFocusId]}</strong>
            </div>
          ) : (
            <div
              style={{
                fontSize: 11,
                marginBottom: 8,
                color: "#64748b",
              }}
            >
              Select a metric node or chip to generate focused insight cards.
            </div>
          )}

          <div style={{ flex: 1, overflowY: "auto", paddingRight: 2 }}>
            {(activeFocusId ? connectedEdgesList : topRelationships).map(
              (edge) => {
                const companionKey =
                  activeFocusId && edge.from === activeFocusId
                    ? edge.to
                    : edge.from;
                const companionText =
                  HUMAN_EXPLANATIONS[companionKey] || companionKey;
                const positive = edge.r > 0;
                return (
                  <div
                    key={`${edge.id}-${activeFocusId || "top"}`}
                    style={{
                      border: "1px solid #e2e8f0",
                      background: "#fbfdff",
                      borderRadius: 8,
                      padding: "7px 8px",
                      marginBottom: 7,
                    }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 800,
                          color: positive ? "#0369a1" : "#b91c1c",
                          background: positive ? "#ecfeff" : "#fef2f2",
                          borderRadius: 999,
                          padding: "2px 7px",
                        }}
                      >
                        {positive ? "RISES" : "DROPS"}
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#0f172a",
                        }}
                      >
                        {companionText}
                      </span>
                      <span
                        style={{
                          marginLeft: "auto",
                          fontSize: 10,
                          color: "#64748b",
                        }}
                      >
                        {(edge.absR * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                );
              },
            )}

            {(activeFocusId ? connectedEdgesList : topRelationships).length ===
              0 && (
              <div
                style={{
                  fontSize: 11,
                  color: "#94a3b8",
                  textAlign: "center",
                  padding: 16,
                }}
              >
                No relationship cards match the current criteria.
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 1100px) {
          .rel-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}

// PEARSON PRODUCT MOMENT COEFFICIENT MATHEMATICS ENGINE
function calculatePearsonCorrelation(vectorX, vectorY) {
  const sampleLength = vectorX.length;
  if (sampleLength === 0) return 0;

  const meanX = vectorX.reduce((sum, v) => sum + v, 0) / sampleLength;
  const meanY = vectorY.reduce((sum, v) => sum + v, 0) / sampleLength;

  let numerator = 0;
  let squaredVarianceX = 0;
  let squaredVarianceY = 0;
  for (let i = 0; i < sampleLength; i++) {
    const diffX = vectorX[i] - meanX;
    const diffY = vectorY[i] - meanY;
    numerator += diffX * diffY;
    squaredVarianceX += diffX * diffX;
    squaredVarianceY += diffY * diffY;
  }

  const denominator = Math.sqrt(squaredVarianceX * squaredVarianceY);
  return denominator === 0 ? 0 : numerator / denominator;
}
